import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import { 
  Settings, 
  Zap, 
  Shield, 
  Search, 
  Brain, 
  Wrench, 
  BarChart3,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RotateCcw,
  Filter,
  MoreHorizontal
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isSystemAdminRole } from "@/lib/roles";

interface MCPTool {
  id: number;
  toolName: string;
  displayName: string;
  description: string;
  serverName: string;
  category: string;
  isEnabled: boolean;
  isSystemCritical: boolean;
  lastModifiedBy: number;
  lastModifiedAt: string;
  createdAt: string;
}

interface ToolCategory {
  category: string;
  count: number;
  enabled: number;
}

const categoryIcons: Record<string, React.ReactNode> = {
  knowledge: <Brain className="h-4 w-4" />,
  search: <Search className="h-4 w-4" />,
  automation: <Wrench className="h-4 w-4" />,
  analysis: <BarChart3 className="h-4 w-4" />,
  general: <Settings className="h-4 w-4" />,
};

const categoryColors: Record<string, string> = {
  knowledge: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  search: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  automation: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  analysis: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  general: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

export default function MCPToolManagement() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkSelection, setBulkSelection] = useState<Set<number>>(new Set());

  // Check if user has required permissions
  const isSystemAdmin = isSystemAdminRole(user?.role);

  // Show access denied if not system admin
  if (!isSystemAdmin) {
    return (
      <div className="p-8 space-y-6">
        <Alert variant="destructive">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <strong>Access Denied:</strong> You need SYSTEM_ADMIN privileges to access MCP tool management.
            <br />
            <strong>Current role:</strong> {user?.role || 'Unknown'}
            <br />
            <strong>Required role:</strong> SYSTEM_ADMIN
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Fetch MCP tools
  const { data: toolsData, isLoading, error } = useQuery<{ tools: MCPTool[] }>({
    queryKey: ["/api/mcp-admin/tools"],
    refetchInterval: 30000, // Refresh every 30 seconds
    retry: (failureCount, error: any) => {
      // Don't retry on 401/403 errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Fetch tool categories
  const { data: categoriesData, error: categoriesError } = useQuery<{ categories: ToolCategory[] }>({
    queryKey: ["/api/mcp-admin/tools/categories"],
    refetchInterval: 30000,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Update tool mutation
  const updateToolMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<MCPTool> }) => {
      return await apiRequest("PATCH", `/api/mcp-admin/tools/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mcp-admin/tools"] });
      queryClient.invalidateQueries({ queryKey: ["/api/mcp-admin/tools/categories"] });
      toast({
        title: "Success",
        description: "Tool settings updated successfully",
      });
    },
    onError: (error: any) => {
      const message = error.message || "Failed to update tool settings";
      const isSystemCritical = message.toLowerCase().includes('system critical');
      toast({
        title: isSystemCritical ? "Cannot Disable Tool" : "Update Failed",
        description: isSystemCritical 
          ? "This tool is required for core system functionality and cannot be disabled."
          : message,
        variant: "destructive",
      });
    },
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async (updates: { id: number; isEnabled: boolean }[]) => {
      return await apiRequest("POST", "/api/mcp-admin/tools/bulk-update", { updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mcp-admin/tools"] });
      queryClient.invalidateQueries({ queryKey: ["/api/mcp-admin/tools/categories"] });
      setBulkSelection(new Set());
      toast({
        title: "Success",
        description: "Tools updated successfully",
      });
    },
    onError: (error: any) => {
      const message = error.message || "Failed to update tools";
      const isSystemCritical = message.toLowerCase().includes('system critical');
      toast({
        title: isSystemCritical ? "Cannot Disable Tools" : "Bulk Update Failed",
        description: isSystemCritical 
          ? "One or more selected tools are required for core functionality and cannot be disabled."
          : message,
        variant: "destructive",
      });
    },
  });

  // Sync tools mutation
  const syncToolsMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/mcp-admin/tools/sync", {});
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/mcp-admin/tools"] });
      queryClient.invalidateQueries({ queryKey: ["/api/mcp-admin/tools/categories"] });
      toast({
        title: "Sync Complete",
        description: data.message,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync tools",
        variant: "destructive",
      });
    },
  });

  const tools = toolsData?.tools || [];
  const categories = categoriesData?.categories || [];
  
  // Log errors for debugging
  useEffect(() => {
    if (categoriesError) {
      console.error('Error fetching tool categories:', categoriesError);
    }
  }, [categoriesError]);

  // Filter tools based on category and search
  const filteredTools = tools.filter(tool => {
    const matchesCategory = selectedCategory === "all" || tool.category === selectedCategory;
    const matchesSearch = searchTerm === "" || 
      tool.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.toolName.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleToggleTool = (toolId: number, isEnabled: boolean) => {
    updateToolMutation.mutate({ id: toolId, updates: { isEnabled } });
  };

  const handleBulkToggle = (enabled: boolean) => {
    const updates = Array.from(bulkSelection).map(id => ({ id, isEnabled: enabled }));
    bulkUpdateMutation.mutate(updates);
  };

  // Only select non-system-critical tools for bulk operations
  const selectableTools = filteredTools.filter(tool => !tool.isSystemCritical);

  const handleSelectAll = () => {
    if (bulkSelection.size === selectableTools.length && selectableTools.length > 0) {
      setBulkSelection(new Set());
    } else {
      setBulkSelection(new Set(selectableTools.map(tool => tool.id)));
    }
  };

  const handleSelectTool = (toolId: number) => {
    const newSelection = new Set(bulkSelection);
    if (newSelection.has(toolId)) {
      newSelection.delete(toolId);
    } else {
      newSelection.add(toolId);
    }
    setBulkSelection(newSelection);
  };

  const getStatusIcon = (tool: MCPTool) => {
    if (tool.isSystemCritical) {
      return <Shield className="h-4 w-4 text-red-500" />;
    }
    return tool.isEnabled ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  };

  const getStatusBadge = (tool: MCPTool) => {
    if (tool.isSystemCritical) {
      return <Badge variant="destructive">System Critical</Badge>;
    }
    return tool.isEnabled ? (
      <Badge variant="default" className="bg-green-100 text-green-800">Enabled</Badge>
    ) : (
      <Badge variant="secondary">Disabled</Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="p-8 space-y-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Failed to load MCP tool settings.</strong>
            <br />
            <strong>Error:</strong> {error instanceof Error ? error.message : 'Unknown error'}
            <br />
            <strong>User role:</strong> {user?.role || 'Unknown'}
            <br />
            {(error as any)?.status === 401 && (
              <>Please ensure you are logged in and have SYSTEM_ADMIN privileges.</>
            )}
            {(error as any)?.status === 403 && (
              <>You do not have sufficient permissions to access this resource.</>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">MCP Tool Management</h1>
          <p className="text-muted-foreground">
            Manage Model Context Protocol tools and their availability
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => syncToolsMutation.mutate()}
            disabled={syncToolsMutation.isPending}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Sync Tools
          </Button>
          {bulkSelection.size > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkToggle(true)}
                disabled={bulkUpdateMutation.isPending}
              >
                Enable Selected ({bulkSelection.size})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkToggle(false)}
                disabled={bulkUpdateMutation.isPending}
              >
                Disable Selected ({bulkSelection.size})
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tool Toggle Explanation */}
      <Alert>
        <Settings className="h-4 w-4" />
        <AlertDescription>
          <strong>How tool toggles work:</strong> Disabling a tool here removes it from the AI's available capabilities
          in this environment. The tool will not appear in Threads or any AI-powered features, and cannot be invoked
          until re-enabled. Changes take effect immediately — no server restart required.
        </AlertDescription>
      </Alert>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tools</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tools.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enabled</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {tools.filter(t => t.isEnabled).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disabled</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {tools.filter(t => !t.isEnabled).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical</CardTitle>
            <Shield className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {tools.filter(t => t.isSystemCritical).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">Search Tools</Label>
              <Input
                id="search"
                placeholder="Search by name, description, or tool name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                className="w-full p-2 border rounded-md"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category.charAt(0).toUpperCase() + cat.category.slice(1)} ({cat.count})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tools Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>MCP Tools ({filteredTools.length})</CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSelectAll}
              disabled={selectableTools.length === 0}
            >
              {bulkSelection.size === selectableTools.length && selectableTools.length > 0 
                ? "Deselect All" 
                : `Select All (${selectableTools.length})`}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Select</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tool Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Server</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTools.map((tool) => (
                  <TableRow key={tool.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={bulkSelection.has(tool.id)}
                        onChange={() => handleSelectTool(tool.id)}
                        disabled={tool.isSystemCritical}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(tool)}
                        {getStatusBadge(tool)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{tool.displayName}</div>
                        <div className="text-sm text-muted-foreground">{tool.toolName}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={categoryColors[tool.category] || categoryColors.general}>
                        <span className="flex items-center gap-1">
                          {categoryIcons[tool.category] || categoryIcons.general}
                          {tool.category.charAt(0).toUpperCase() + tool.category.slice(1)}
                        </span>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{tool.serverName}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-md truncate" title={tool.description}>
                        {tool.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={tool.isEnabled}
                          onCheckedChange={(checked) => handleToggleTool(tool.id, checked)}
                          disabled={tool.isSystemCritical || updateToolMutation.isPending}
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onClick={() => handleToggleTool(tool.id, !tool.isEnabled)}
                              disabled={tool.isSystemCritical}
                            >
                              {tool.isEnabled ? "Disable" : "Enable"} Tool
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* System Critical Tools Warning */}
      {tools.some(t => t.isSystemCritical) && (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            <strong>System Critical Tools:</strong> Some tools are marked as system critical and cannot be disabled. 
            These tools are essential for core functionality and are required for the system to operate properly.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
} 